import { type FC } from "react";
import { Navigate, Route, Routes } from "react-router";

import { FeatureFlags } from "featureFlags";
import CreateEmbeddedVideo from "features/Library/EmbeddedVideos/Create/CreateEmbeddedVideo";
import EditEmbeddedVideo from "features/Library/EmbeddedVideos/Edit/EditEmbeddedVideo";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import NotFoundMessage from "../../components/notFound/NotFoundMessage";
import { RolePermissions } from "../../enums";
import { AssessmentsOverview, CreateAssessment, EditAssessment } from "../../features/Library/Assessments";
import { CommunicationsOverview } from "../../features/Library/Communications";
import CreateEmail from "../../features/Library/Communications/Emails/Create/CreateEmail";
import EmailDetailsWrapper from "../../features/Library/Communications/Emails/Edit/EmailDetailsWrapper";
import { CreateEmailSendSession } from "../../features/Library/Communications/Emails/SendSessions/CreateEmailSendSession";
import CreateMessage from "../../features/Library/Communications/Messages/Create/CreateMessage";
import EditMessage from "../../features/Library/Communications/Messages/Edit/EditMessage";
import { CreateMessageSendSession } from "../../features/Library/Communications/Messages/SendSessions/CreateMessageSendSession";
import CreateExternalLink from "features/Library/ExternalLinks/Create/CreateExternalLink";
import EditExternalLink from "features/Library/ExternalLinks/Edit/EditExternalLink";
import ExternalLinksOverview from "features/Library/ExternalLinks/Overview/ExternalLinksOverview";
import CreateEvent from "../../features/Library/Events/Create/CreateEvent";
import EditEvent from "../../features/Library/Events/Edit/EditEvent";
import EventsOverview from "../../features/Library/Events/Overview/EventsOverview";
import CreateFlow from "../../features/Library/Flows/Create/CreateFlow";
import EditFlow from "../../features/Library/Flows/Edit/FlowDetails";
import { CreatePdf, EditPdf, Pdfs } from "../../features/Library/Pdfs";
import { CreateCampaign, EditCampaign, Campaigns } from "../../features/Library/SimulatedPhishing/Campaigns";
import CreateSurvey from "../../features/Library/Surveys/Create/CreateSurvey";
import EditSurvey from "../../features/Library/Surveys/Edit/EditSurvey";
import SurveysOverview from "../../features/Library/Surveys/Overview/SurveysOverview";
import { CreateVideo, VideoDetails } from "../../features/Library/Videos";
import FlowsOverview from "./flows/flowsOverview/FlowsOverview";
import CreateSend from "features/Library/SimulatedPhishing/Campaigns/Create/Send/CreateSend";

import VideosOverview from "features/Library/Videos/Overview/VideosOverview";
import { catchAll } from "../../utils/routeHelpers";
import { RestrictedResource } from "components";

const LibraryContent: FC = () => {
  const embeddedVideosEnabled = useFeatureFlag(FeatureFlags.EmbeddedVideos);
  const linkAssetTypeEnabled = useFeatureFlag(FeatureFlags.LinkAssetType);
  return (
    <div className="route-content-container">
      <Routes>
        <Route path="flows">
          <Route
            index
            element={
              <RestrictedResource permissions={[RolePermissions.FlowsView]}>
                <FlowsOverview />
              </RestrictedResource>
            }
          />
          <Route
            path="createFlow"
            element={
              <RestrictedResource permissions={[RolePermissions.FlowsCreate]}>
                <CreateFlow />
              </RestrictedResource>
            }
          />
          {catchAll(
            ":id",
            <RestrictedResource permissions={[RolePermissions.FlowsView]}>
              <EditFlow />
            </RestrictedResource>,
          )}
        </Route>
        <Route path="events">
          <Route
            index
            element={
              <RestrictedResource permissions={[RolePermissions.EventsView]}>
                <EventsOverview />
              </RestrictedResource>
            }
          />
          <Route
            path="create"
            element={
              <RestrictedResource permissions={[RolePermissions.EventsCreate]}>
                <CreateEvent />
              </RestrictedResource>
            }
          />
          {catchAll(
            ":id",
            <RestrictedResource permissions={[RolePermissions.EventsView]}>
              <EditEvent />
            </RestrictedResource>,
          )}
        </Route>
        <Route path="assets">
          <Route path="videos">
            <Route
              index
              element={
                <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                  <VideosOverview />
                </RestrictedResource>
              }
            />
            <Route
              path="createAsset"
              element={
                <RestrictedResource permissions={[RolePermissions.AssetsCreate]}>
                  {
                    // @ts-ignore
                    <CreateVideo />
                  }
                </RestrictedResource>
              }
            />
            {catchAll(
              ":id",
              <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                <VideoDetails />
              </RestrictedResource>,
            )}
          </Route>
          {embeddedVideosEnabled && (
            <Route path="videos/embedded">
              <Route
                index
                element={
                  <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                    <VideosOverview />
                  </RestrictedResource>
                }
              />
              <Route
                path="create"
                element={
                  <RestrictedResource permissions={[RolePermissions.AssetsCreate]}>
                    <CreateEmbeddedVideo />
                  </RestrictedResource>
                }
              />
              {catchAll(
                ":id",
                <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                  <EditEmbeddedVideo />
                </RestrictedResource>,
              )}
            </Route>
          )}
          <Route path="pdfs">
            <Route
              index
              element={
                <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                  <Pdfs />
                </RestrictedResource>
              }
            />
            <Route
              path="create"
              element={
                <RestrictedResource permissions={[RolePermissions.AssetsCreate]}>
                  <CreatePdf />
                </RestrictedResource>
              }
            />
            {catchAll(
              ":id",
              <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                <EditPdf />
              </RestrictedResource>,
            )}
          </Route>
          <Route path="assessments">
            <Route
              index
              element={
                <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                  <AssessmentsOverview />
                </RestrictedResource>
              }
            />

            <Route
              path="create"
              element={
                <RestrictedResource permissions={[RolePermissions.AssetsCreate]}>
                  <CreateAssessment />
                </RestrictedResource>
              }
            />
            {catchAll(
              ":id",
              <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                <EditAssessment />
              </RestrictedResource>,
            )}
          </Route>
          <Route path="surveys">
            <Route
              index
              element={
                <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                  <SurveysOverview />
                </RestrictedResource>
              }
            />

            <Route
              path="create"
              element={
                <RestrictedResource permissions={[RolePermissions.AssetsCreate]}>
                  <CreateSurvey />
                </RestrictedResource>
              }
            />
            {catchAll(
              ":id",
              <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                <EditSurvey />
              </RestrictedResource>,
            )}
          </Route>
          {linkAssetTypeEnabled && (
            <Route path="links">
              <Route
                index
                element={
                  <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                    <ExternalLinksOverview />
                  </RestrictedResource>
                }
              />
              <Route
                path="create"
                element={
                  <RestrictedResource permissions={[RolePermissions.AssetsCreate]}>
                    <CreateExternalLink />
                  </RestrictedResource>
                }
              />
              {catchAll(
                ":id",
                <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                  <EditExternalLink />
                </RestrictedResource>,
              )}
            </Route>
          )}
        </Route>
        <Route path="communications">
          <Route
            index
            element={
              <RestrictedResource permissions={[RolePermissions.CommunicationsView]}>
                <CommunicationsOverview />
              </RestrictedResource>
            }
          />
          <Route path="emails">
            <Route
              index
              element={
                <RestrictedResource permissions={[RolePermissions.CommunicationsView]}>
                  <CommunicationsOverview />
                </RestrictedResource>
              }
            />
            <Route
              path="create"
              element={
                <RestrictedResource permissions={[RolePermissions.CommunicationsCreate]}>
                  <CreateEmail />
                </RestrictedResource>
              }
            />
            <Route
              path=":id/send-sessions/create"
              element={
                <RestrictedResource permissions={[RolePermissions.CommunicationsManage]}>
                  <CreateEmailSendSession />
                </RestrictedResource>
              }
            />
            {catchAll(
              ":id",
              <RestrictedResource permissions={[RolePermissions.CommunicationsView]}>
                <EmailDetailsWrapper />
              </RestrictedResource>,
            )}
            <Route path="*" element={<Navigate to="/content/communications/emails" replace />} />
          </Route>

          <Route path="messages">
            <Route
              index
              element={
                <RestrictedResource permissions={[RolePermissions.CommunicationsView]}>
                  <CommunicationsOverview />
                </RestrictedResource>
              }
            />
            <Route
              path="create"
              element={
                <RestrictedResource permissions={[RolePermissions.CommunicationsCreate]}>
                  <CreateMessage />
                </RestrictedResource>
              }
            />
            <Route
              path=":id/send-sessions/create"
              element={
                <RestrictedResource permissions={[RolePermissions.CommunicationsManage]}>
                  <CreateMessageSendSession />
                </RestrictedResource>
              }
            />
            {catchAll(
              ":id",
              <RestrictedResource permissions={[RolePermissions.CommunicationsView]}>
                <EditMessage />
              </RestrictedResource>,
            )}
            <Route path="*" element={<Navigate to="/content/communications/messages" replace />} />
          </Route>

          <Route path="*" element={<Navigate to="/content/communications/emails" replace />} />
        </Route>
        <Route path="simulated-phishing/campaigns">
          <Route index element={<Campaigns />} />
          <Route path="create" element={<CreateCampaign />} />
          {catchAll(":id", <EditCampaign />)}
          <Route path=":id/sends/create" element={<CreateSend />} />
        </Route>
        <Route path="*" element={<NotFoundMessage />} />
      </Routes>
    </div>
  );
};

export default LibraryContent;
